import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotify } from '../context/NotifyContext';
import { profileApi, ProfileUser } from '../api/profile.api';
import Logo from '../components/Logo';
import ThemeToggle from '../components/ThemeToggle';
import UserMenu from '../components/UserMenu';
import usePageMeta from '../hooks/usePageMeta';

const ProfilePage: React.FC = () => {
  usePageMeta({ title: 'Your profile — OnTheWeb', index: false });

  const { user, updateUser } = useAuth();
  const { notify } = useNotify();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name || '');
  const [businessName, setBusinessName] = useState(user?.businessName || '');
  const [address, setAddress] = useState(user?.address || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [countryCode, setCountryCode] = useState(user?.countryCode || '+91');
  const [email, setEmail] = useState(user?.email || '');

  const [saving, setSaving] = useState(false);

  const [phoneDraft, setPhoneDraft] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneStep, setPhoneStep] = useState<'idle' | 'otp'>('idle');
  const [phoneBusy, setPhoneBusy] = useState(false);

  const [emailDraft, setEmailDraft] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [emailStep, setEmailStep] = useState<'idle' | 'otp'>('idle');
  const [emailBusy, setEmailBusy] = useState(false);

  const applyUser = (next: ProfileUser) => {
    updateUser({
      id: String(next.id),
      name: next.name,
      phone: next.phone,
      countryCode: next.countryCode,
      email: next.email,
      businessName: next.businessName,
      address: next.address,
      role: next.role,
      profilePicture: next.profilePicture,
    });
    setName(next.name || '');
    setBusinessName(next.businessName || '');
    setAddress(next.address || '');
    setPhone(next.phone || '');
    setCountryCode(next.countryCode || '+91');
    setEmail(next.email || '');
  };

  useEffect(() => {
    profileApi
      .get()
      .then(applyUser)
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const next = await profileApi.update({ name, businessName, address });
      applyUser(next);
      notify.success('Profile saved.');
    } catch (err: any) {
      notify.error(err.response?.data?.message || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  const sendPhoneOtp = async () => {
    setPhoneBusy(true);
    try {
      await profileApi.sendContactOtp({ channel: 'phone', phone: phoneDraft, countryCode: '+91' });
      setPhoneStep('otp');
      notify.success('OTP sent to your mobile. Use 123456 until SMS is enabled.');
    } catch (err: any) {
      notify.error(err.response?.data?.message || 'Could not send OTP.');
    } finally {
      setPhoneBusy(false);
    }
  };

  const verifyPhoneOtp = async () => {
    setPhoneBusy(true);
    try {
      const next = await profileApi.verifyContactOtp({
        channel: 'phone',
        phone: phoneDraft,
        countryCode: '+91',
        otp: phoneOtp,
      });
      applyUser(next);
      setPhoneDraft('');
      setPhoneOtp('');
      setPhoneStep('idle');
      notify.success('Mobile number updated.');
    } catch (err: any) {
      notify.error(err.response?.data?.message || 'Could not verify OTP.');
    } finally {
      setPhoneBusy(false);
    }
  };

  const sendEmailOtp = async () => {
    setEmailBusy(true);
    try {
      await profileApi.sendContactOtp({ channel: 'email', email: emailDraft });
      setEmailStep('otp');
      notify.success('OTP sent to your email. Use 123456 until email sending is enabled.');
    } catch (err: any) {
      notify.error(err.response?.data?.message || 'Could not send OTP.');
    } finally {
      setEmailBusy(false);
    }
  };

  const verifyEmailOtp = async () => {
    setEmailBusy(true);
    try {
      const next = await profileApi.verifyContactOtp({ channel: 'email', email: emailDraft, otp: emailOtp });
      applyUser(next);
      setEmailDraft('');
      setEmailOtp('');
      setEmailStep('idle');
      notify.success('Email updated.');
    } catch (err: any) {
      notify.error(err.response?.data?.message || 'Could not verify OTP.');
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <div className="nova-page">
      <nav className="nova-nav relative border-b border-nova-border">
        <div className="nova-nav-inner">
          <button type="button" onClick={() => navigate('/dashboard')} className="min-w-0">
            <Logo />
          </button>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </nav>

      <div className="max-w-xl mx-auto px-4 py-10 pt-24">
        <h1 className="font-display text-3xl font-extrabold text-nova-text mb-2">Profile</h1>
        <p className="text-nova-text2 mb-8">Update your account. Mobile and email change with an OTP.</p>

        <form onSubmit={handleSave} className="nova-card space-y-5 mb-6">
          <label className="block">
            <span className="nova-label">Name</span>
            <input className="nova-input" value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="block">
            <span className="nova-label">Business name</span>
            <input className="nova-input" value={businessName} onChange={(e) => setBusinessName(e.target.value)} required />
          </label>
          <label className="block">
            <span className="nova-label">Address</span>
            <textarea className="nova-input min-h-[88px]" value={address} onChange={(e) => setAddress(e.target.value)} />
          </label>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </form>

        <div className="nova-card space-y-6">
          <div>
            <p className="nova-label">Mobile</p>
            <p className="text-nova-text mb-3">{phone ? `${countryCode} ${phone}` : 'Not set'}</p>
            {phoneStep === 'idle' ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  className="nova-input"
                  placeholder="New 10-digit number"
                  value={phoneDraft}
                  onChange={(e) => setPhoneDraft(e.target.value.replace(/\D/g, '').slice(0, 10))}
                />
                <button type="button" className="btn-ghost flex-shrink-0" disabled={phoneBusy || phoneDraft.length !== 10} onClick={sendPhoneOtp}>
                  {phoneBusy ? 'Sending...' : 'Send OTP'}
                </button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  className="nova-input"
                  placeholder="OTP"
                  value={phoneOtp}
                  onChange={(e) => setPhoneOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
                <button type="button" className="btn-primary flex-shrink-0" disabled={phoneBusy || phoneOtp.length !== 6} onClick={verifyPhoneOtp}>
                  {phoneBusy ? 'Verifying...' : 'Verify'}
                </button>
                <button
                  type="button"
                  className="btn-ghost flex-shrink-0"
                  onClick={() => {
                    setPhoneStep('idle');
                    setPhoneOtp('');
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div>
            <p className="nova-label">Email</p>
            <p className="text-nova-text mb-3">{email || 'Not set'}</p>
            {emailStep === 'idle' ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  className="nova-input"
                  type="email"
                  placeholder="New email"
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                />
                <button type="button" className="btn-ghost flex-shrink-0" disabled={emailBusy || !emailDraft.includes('@')} onClick={sendEmailOtp}>
                  {emailBusy ? 'Sending...' : 'Send OTP'}
                </button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  className="nova-input"
                  placeholder="OTP"
                  value={emailOtp}
                  onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
                <button type="button" className="btn-primary flex-shrink-0" disabled={emailBusy || emailOtp.length !== 6} onClick={verifyEmailOtp}>
                  {emailBusy ? 'Verifying...' : 'Verify'}
                </button>
                <button
                  type="button"
                  className="btn-ghost flex-shrink-0"
                  onClick={() => {
                    setEmailStep('idle');
                    setEmailOtp('');
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
